// One Claude conversation from 每日看板 (/owner-board/t/:threadId, owner-only).
// Magnus replies here with a per-message model + effort; the runner on his PC
// (workspace scripts/agents/workbench_runner.py) answers in the same session.
// Messages sent while a turn is running wait and go out on the next turn.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import api from '../services/api';
import {
  AgentMessage,
  AgentThread,
  ApprovalCard,
  ApprovalMeta,
  Composer,
  LogBox,
  RichText,
  WorkstreamTag,
  clockTime,
  errMessage,
  modelBadge,
  useAgentConfig,
} from '../components/agent/agentShared';

const AgentThreadPage: React.FC = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const config = useAgentConfig();
  const [thread, setThread] = useState<AgentThread | null>(null);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [model, setModel] = useState<string>('opus');
  const [effort, setEffort] = useState<string>('medium');
  const [draft, setDraft] = useState('');
  const [openActivity, setOpenActivity] = useState<Record<number, boolean>>({});
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const lastCount = useRef(0);
  const pickerTouched = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/agent-threads/${threadId}`);
      setThread(res.data.thread);
      setMessages(res.data.messages || []);
      if (!pickerTouched.current) {
        setModel(res.data.thread.model);
        setEffort(res.data.thread.effort);
      }
      setError(null);
    } catch (err: any) {
      setError(err?.response?.status === 404 ? '找不到這個對話' : errMessage(err));
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  useEffect(() => {
    load();
  }, [load]);

  const moving = thread?.status === 'running' || thread?.status === 'queued';
  useEffect(() => {
    const t = setInterval(load, moving ? 2000 : 8000);
    return () => clearInterval(t);
  }, [load, moving]);

  // Follow new messages to the bottom.
  useEffect(() => {
    if (messages.length !== lastCount.current) {
      lastCount.current = messages.length;
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages.length]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const patch = (body: Record<string, unknown>) => act(() => api.patch(`/api/agent-threads/${threadId}`, body));

  const decide = (approvalId: number, body: Record<string, unknown>) =>
    act(() => api.post(`/api/agent-threads/${threadId}/decide`, { approval_id: approvalId, ...body }));

  const send = async (v: { text: string; model: string; effort: string; images: string[] }) => {
    try {
      await api.post(`/api/agent-threads/${threadId}/messages`, v);
      await load();
    } catch (err) {
      setError(errMessage(err));
      throw err;
    }
  };

  const stop = (redirect?: string) =>
    act(async () => {
      await api.post(`/api/agent-threads/${threadId}/stop`, redirect ? { text: redirect } : {});
      if (redirect) setDraft('');
    });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!thread) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error || '找不到這個對話'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/owner-board')}>
          回每日看板
        </Button>
      </Box>
    );
  }

  const lastActivityId = [...messages].reverse().find((m) => m.role === 'activity')?.id;

  const renderMessage = (m: AgentMessage) => {
    if (m.role === 'user') {
      const decision = m.meta?.kind === 'decision';
      const label = m.meta?.display || m.content;
      if (decision) {
        return (
          <Box key={m.id} sx={{ textAlign: 'center' }}>
            <Chip size="small" label={label} variant="outlined" />
          </Box>
        );
      }
      return (
        <Box key={m.id} sx={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              px: 1.5,
              py: 1,
              borderRadius: 3,
              borderBottomRightRadius: 6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <Typography variant="body2">{label}</Typography>
            {!!m.meta?.images?.length && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                {m.meta.images.map((src: string, i: number) => (
                  <Box
                    key={i}
                    component="img"
                    src={src}
                    alt=""
                    onClick={() => setZoomImage(src)}
                    sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, cursor: 'zoom-in' }}
                  />
                ))}
              </Stack>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.25 }}>
            {m.meta?.pending
              ? thread.status === 'running'
                ? '下一輪送出'
                : '等待送出'
              : m.meta?.skipped
              ? '未送出'
              : clockTime(m.created_at)}
            {m.meta?.model ? ` · ${modelBadge(m.meta.model, m.meta.effort)}` : ''}
          </Typography>
        </Box>
      );
    }
    if (m.role === 'assistant') {
      return (
        <Box key={m.id} sx={{ alignSelf: 'flex-start', maxWidth: '92%' }}>
          <Box
            sx={{
              bgcolor: 'grey.100',
              px: 1.5,
              py: 1.25,
              borderRadius: 3,
              borderBottomLeftRadius: 6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1.75 }}>
              <RichText text={m.content} />
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {modelBadge(m.meta?.model, m.meta?.effort)}
            {m.meta?.duration_s ? ` · ${m.meta.duration_s}s` : ''} · {clockTime(m.created_at)}
          </Typography>
        </Box>
      );
    }
    if (m.role === 'activity') {
      const lines = (m.content || '').split('\n').filter((l) => l.trim());
      const steps = lines.filter((l) => l.startsWith('🔧')).length;
      const live = thread.status === 'running' && m.id === lastActivityId;
      const isOpen = !!openActivity[m.id];
      return (
        <Box key={m.id} sx={{ alignSelf: 'stretch' }}>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            onClick={() => setOpenActivity((o) => ({ ...o, [m.id]: !isOpen }))}
            sx={{ cursor: 'pointer', color: 'text.secondary' }}
          >
            {live ? (
              <AutorenewRoundedIcon
                sx={{ fontSize: 16, animation: 'spin 1.6s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }}
                color="primary"
              />
            ) : (
              <BuildOutlinedIcon sx={{ fontSize: 15 }} />
            )}
            <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {live ? thread.status_line || '執行中…' : `${steps} 個步驟`}
              {` · ${modelBadge(m.meta?.model, m.meta?.effort)}`}
              {m.meta?.duration_s ? ` · ${m.meta.duration_s}s` : ''}
            </Typography>
            <Typography variant="caption" color="primary">
              {isOpen ? '收起' : '展開'}
            </Typography>
          </Stack>
          <Collapse in={isOpen} unmountOnExit>
            <LogBox text={m.content} />
          </Collapse>
        </Box>
      );
    }
    if (m.role === 'approval') {
      const meta = m.meta as ApprovalMeta;
      const isOpen = meta.state === 'open';
      return (
        <Box key={m.id} sx={{ alignSelf: 'stretch' }}>
          <ApprovalCard
            meta={meta}
            busy={busy}
            onZoom={setZoomImage}
            onApprove={isOpen ? (note) => decide(m.id, { action: 'approve', note }) : undefined}
            onAnswer={isOpen ? (answer, note) => decide(m.id, { action: 'answer', answer, note }) : undefined}
            onRevise={isOpen ? (note) => send({ text: note, model, effort, images: [] }) : undefined}
            onDone={isOpen ? () => decide(m.id, { action: 'done' }) : undefined}
          />
        </Box>
      );
    }
    return (
      <Typography key={m.id} variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
        {m.content}
      </Typography>
    );
  };

  return (
    // Single-column grid with a 0 minimum: long no-wrap titles must not widen the page on phones.
    <Box sx={{ maxWidth: 820, mx: 'auto', minWidth: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      {/* ── header ── */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <IconButton aria-label="回每日看板" onClick={() => navigate('/owner-board')}>
          <ArrowBackRoundedIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
            {thread.title}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <WorkstreamTag name={thread.workstream} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {thread.status === 'running'
                ? '執行中'
                : thread.status === 'queued'
                ? '排隊中'
                : thread.status === 'awaiting_approval'
                ? '等你決定'
                : thread.status === 'failed'
                ? '沒跑完'
                : '待命'}
            </Typography>
          </Stack>
        </Box>
        <Tooltip title={thread.pinned ? '取消釘選' : '釘選'}>
          <IconButton onClick={() => patch({ pinned: !thread.pinned })}>
            {thread.pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="封存">
          <span>
            <IconButton
              disabled={moving}
              onClick={async () => {
                await patch({ archived: true });
                navigate('/owner-board');
              }}
            >
              <ArchiveOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── messages ── */}
      <Stack spacing={1.5} sx={{ px: { xs: 0.5, md: 1 }, pb: 2 }}>
        {messages.map(renderMessage)}
        {thread.status === 'failed' && (
          <Box sx={{ textAlign: 'center' }}>
            <Button variant="outlined" size="small" disabled={busy} onClick={() => act(() => api.post(`/api/agent-threads/${threadId}/retry`))}>
              重試
            </Button>
          </Box>
        )}
        <div ref={bottomRef} />
      </Stack>

      {/* ── composer ── */}
      <Box sx={{ position: 'sticky', bottom: { xs: 8, md: 16 }, zIndex: 5 }}>
        {moving && (
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 0.75 }}>
            {draft.trim() && thread.status === 'running' && (
              <Button size="small" variant="contained" color="warning" disableElevation disabled={busy} onClick={() => stop(draft.trim())}>
                停止並改方向
              </Button>
            )}
            <Button size="small" variant="outlined" color="error" disabled={busy || thread.status_line === '停止中…'} onClick={() => stop()}>
              {thread.status === 'running' ? '停止' : '取消排隊'}
            </Button>
          </Stack>
        )}
        <Composer
          config={config}
          model={model}
          effort={effort}
          onModelChange={(m) => {
            pickerTouched.current = true;
            setModel(m);
          }}
          onEffortChange={(e) => {
            pickerTouched.current = true;
            setEffort(e);
          }}
          text={draft}
          onTextChange={setDraft}
          onSubmit={send}
          placeholder={thread.status === 'running' ? '回覆（這一輪做完後送出）…' : '回覆…'}
          extraLeft={
            <Stack direction="row" alignItems="center" sx={{ ml: 'auto' }}>
              <Typography variant="caption" color="text.secondary">
                完成時 LINE
              </Typography>
              <Switch size="small" checked={thread.notify_done} onChange={(e) => patch({ notify_done: e.target.checked })} />
            </Stack>
          }
        />
      </Box>

      <Dialog open={!!zoomImage} onClose={() => setZoomImage(null)} maxWidth="md">
        {zoomImage && <Box component="img" src={zoomImage} alt="preview" sx={{ width: '100%', display: 'block' }} />}
      </Dialog>
    </Box>
  );
};

export default AgentThreadPage;
